<script setup>
// Used only for creating new cards from the kanban board.
// The edit flow is now inline (see PipelineCardHero, CardInfoPanel).
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PipelineSelector from './PipelineSelector.vue';
import { emitter } from 'shared/helpers/mitt';
import conversationAPI from '../../../../api/inbox/conversation';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ConfirmModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import {
  formatInReportingTimezone,
  formatLocalizedCurrency,
  normalizeLocale,
  parseLocalizedNumber,
  zonedDateTimeToUtc,
} from '../utils/localeFormatting';

const props = defineProps({
  pipelineId: {
    type: [String, Number],
    required: true,
  },
  stage: {
    type: String,
    required: true,
  },
  position: {
    type: Number,
    default: 0,
  },
  initialData: {
    type: Object,
    default: null,
  },
  initialDate: {
    type: [String, Date, Number],
    default: null,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  currentConversation: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['saved', 'close']);

const { t, locale } = useI18n();
const store = useStore();

const loading = ref(false);
const loadingAgents = ref(false);
const agentsLoadError = ref('');
const formErrors = ref({});
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const currentAccount = computed(
  () =>
    store.getters['accounts/getAccount']?.(store.getters.getCurrentAccountId) ||
    store.getters.getCurrentAccount ||
    {}
);
const reportingTimezone = computed(
  () =>
    currentAccount.value.reporting_timezone ||
    currentAccount.value.settings?.reporting_timezone ||
    'UTC'
);
const activeLocale = computed(() => normalizeLocale(locale.value));

// Computed para obter as etapas do funil selecionado
const availableStages = computed(() => {
  if (!selectedPipeline.value?.stages) return [];

  return Object.entries(selectedPipeline.value.stages)
    .map(([id, stage]) => ({
      id,
      name: stage.name,
      position: stage.position,
    }))
    .sort((a, b) => a.position - b.position);
});

// Refs para controle da conversa
const showConversationInput = ref(
  Boolean(
    props.currentConversation ||
      props.initialData?.item_details?.conversation_id
  )
);
const conversations = ref([]);
const loadingConversations = ref(false);
const conversationsLoadError = ref('');

const stageOptions = computed(() =>
  availableStages.value.map(stage => ({
    value: stage.id,
    label: stage.name,
  }))
);
const agentOptions = computed(() => [
  {
    value: '',
    label: t('PIPELINE.FORM.AGENT.PLACEHOLDER'),
  },
  ...(store.getters['agents/getAgents'] || []).map(agent => ({
    value: agent.id,
    label: agent.name,
  })),
]);
const conversationOptions = computed(() => [
  {
    value: '',
    label: t('PIPELINE.FORM.CONVERSATION.PLACEHOLDER'),
  },
  ...conversations.value.map(conversation => {
    const sender = conversation.meta?.sender || {};
    return {
      value: conversation.id,
      label: `#${conversation.id} - ${
        sender.name || sender.email || t('PIPELINE.FORM.CONVERSATION.UNKNOWN')
      }`,
    };
  }),
]);

const currencyCodeFrom = value => {
  const raw = typeof value === 'object' ? value?.code : value;
  const code = String(raw || '')
    .trim()
    .toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : null;
};

const accountCurrencyCode =
  currencyCodeFrom(currentAccount.value.currency) ||
  currencyCodeFrom(currentAccount.value.settings?.currency) ||
  'BRL';
const initialCurrencyCode =
  currencyCodeFrom(props.initialData?.currency) ||
  currencyCodeFrom(props.initialData?.item_details?.currency) ||
  currencyCodeFrom(props.initialData?.item_details?.offers?.[0]?.currency) ||
  accountCurrencyCode;
const initialOfferCurrencyCodes = (
  props.initialData?.item_details?.offers || []
)
  .map(offer => currencyCodeFrom(offer.currency))
  .filter(Boolean);
const currencyCodes = [
  ...new Set([
    'BRL',
    'USD',
    'EUR',
    accountCurrencyCode,
    initialCurrencyCode,
    ...initialOfferCurrencyCodes,
  ]),
];
const knownCurrencySymbols = { BRL: 'R$', USD: '$', EUR: '€' };
const currencyOptions = currencyCodes.map(code => {
  let symbol = knownCurrencySymbols[code];
  if (!symbol) {
    try {
      symbol = new Intl.NumberFormat(activeLocale.value, {
        style: 'currency',
        currency: code,
        currencyDisplay: 'narrowSymbol',
      })
        .formatToParts(0)
        .find(part => part.type === 'currency')?.value;
    } catch {
      symbol = code;
    }
  }
  return { symbol: symbol || code, code };
});
const currencyOptionFor = value => {
  const code = currencyCodeFrom(value) || initialCurrencyCode;
  return (
    currencyOptions.find(currency => currency.code === code) ||
    currencyOptions[0]
  );
};
const initialCurrency = currencyOptionFor(initialCurrencyCode);
const initialValue =
  props.initialData?.expected_revenue ??
  props.initialData?.item_details?.value ??
  null;

// Ref para controlar a visibilidade do input de valor
const showValueInput = ref(
  initialValue !== null && initialValue !== undefined && initialValue !== ''
);

const currencySelectOptions = currencyOptions.map(currency => ({
  value: currency.code,
  label: `${currency.code} (${currency.symbol})`,
}));

// Helper para extrair data e hora de uma string ISO
const extractDateAndTime = isoString => {
  if (!isoString) return { date: '', time: '' };
  const date = formatInReportingTimezone(
    isoString,
    'yyyy-MM-dd',
    reportingTimezone.value
  );
  const time = formatInReportingTimezone(
    isoString,
    'HH:mm',
    reportingTimezone.value
  );
  return { date, time: time === '00:00' ? '' : time };
};

// Extrair data/hora do initialData
const initialDeadlineValue =
  props.initialData?.item_details?.deadline_at ||
  props.initialData?.item_details?.deadline;
const initialDeadline = extractDateAndTime(initialDeadlineValue);

const normalizeInitialDate = value => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value);

  return formatInReportingTimezone(
    value,
    'yyyy-MM-dd',
    reportingTimezone.value
  );
};

const suggestedSchedule = date => {
  if (!date) return { date: '', time: '09:00' };
  const defaultDeadline = zonedDateTimeToUtc(
    date,
    '09:00',
    reportingTimezone.value
  );
  if (defaultDeadline?.getTime() > Date.now()) {
    return { date, time: '09:00' };
  }

  const reportingToday = formatInReportingTimezone(
    new Date(),
    'yyyy-MM-dd',
    reportingTimezone.value
  );
  if (date !== reportingToday) return { date, time: '09:00' };

  const halfHour = 30 * 60 * 1000;
  const nextSlot = new Date(Math.ceil((Date.now() + 1) / halfHour) * halfHour);
  return {
    date: formatInReportingTimezone(
      nextSlot,
      'yyyy-MM-dd',
      reportingTimezone.value
    ),
    time: formatInReportingTimezone(nextSlot, 'HH:mm', reportingTimezone.value),
  };
};

const agendaSchedule = suggestedSchedule(
  normalizeInitialDate(props.initialDate)
);
const initialScheduleDate = initialDeadline.date || agendaSchedule.date;
const initialScheduleTime = initialDeadline.time || agendaSchedule.time;

// Refs para controle de agendamento
const showScheduleInput = ref(Boolean(initialScheduleDate));
const scheduleDate = ref(initialScheduleDate);
const scheduleTime = ref(initialScheduleTime);
const initialScheduleSnapshot = {
  enabled: Boolean(initialDeadline.date),
  date: initialScheduleDate,
  time: initialScheduleTime,
};
const scheduleMatchesHistoricalDeadline = () =>
  props.isEditing &&
  Boolean(initialDeadlineValue) &&
  showScheduleInput.value === initialScheduleSnapshot.enabled &&
  scheduleDate.value === initialScheduleSnapshot.date &&
  scheduleTime.value === initialScheduleSnapshot.time;

// Fase 2.2 — Previsão de fechamento (data em que o vendedor espera fechar)
// Normaliza pra YYYY-MM-DD se vier como ISO datetime do backend.
const forecastCloseDate = ref(
  (() => {
    const raw = props.initialData?.forecast_close_date;
    if (!raw) return '';
    // Se for ISO completo (YYYY-MM-DDTHH:MM:SS...), pegar só a parte da data
    return String(raw).slice(0, 10);
  })()
);

// Opções rápidas de hora
const quickTimeOptions = [
  '08:00',
  '09:00',
  '10:00',
  '14:00',
  '15:00',
  '16:00',
  '18:00',
];

// Atualizar o form.value para incluir as ofertas e datas
const form = ref({
  title: props.initialData?.title || '',
  description:
    props.initialData?.item_details?.description ||
    props.initialData?.description ||
    '',
  pipeline_id: props.pipelineId,
  pipeline_stage: props.stage,
  position: props.position,
  item_details: {
    title: props.initialData?.title || '',
    description:
      props.initialData?.item_details?.description ||
      props.initialData?.description ||
      '',
    value: initialValue,
    currency: initialCurrency,
    offers: [],
    priority:
      props.initialData?.item_details?.priority ||
      props.initialData?.priority ||
      'none',
    agent_id:
      props.initialData?.item_details?.agent_id ||
      props.initialData?.owner_id ||
      '',
    conversation_id: props.initialData?.item_details?.conversation_id || '',
  },
  custom_attributes: props.initialData?.custom_attributes || {},
});

// Ref para nova oferta (Fase 3.5 adiciona `url` opcional)
const newOffer = ref({
  description: '',
  value: '',
  currency: initialCurrency,
  url: '',
});

const selectedCurrencyCode = computed({
  get: () => newOffer.value.currency?.code || initialCurrencyCode,
  set: code => {
    newOffer.value.currency = currencyOptionFor(code);
  },
});

// Refs para controle das ofertas
const offers = ref([]);

const syncOfferTotals = () => {
  if (offers.value.length === 0) {
    form.value.item_details.value = null;
    form.value.item_details.currency = newOffer.value.currency;
    return;
  }

  form.value.item_details.value = offers.value.reduce(
    (sum, offer) => sum + (Number(offer.value) || 0),
    0
  );
  form.value.item_details.currency = offers.value[0].currency;
  newOffer.value.currency = offers.value[0].currency;
};

const initialDraftSnapshot = ref(null);
const captureDraftSnapshot = () =>
  JSON.stringify({
    form: form.value,
    offers: offers.value,
    newOffer: newOffer.value,
    showValueInput: showValueInput.value,
    showScheduleInput: showScheduleInput.value,
    scheduleDate: scheduleDate.value,
    scheduleTime: scheduleTime.value,
    forecastCloseDate: forecastCloseDate.value,
    showConversationInput: showConversationInput.value,
  });
const isDirty = computed(
  () =>
    initialDraftSnapshot.value !== null &&
    captureDraftSnapshot() !== initialDraftSnapshot.value
);
const discardConfirmation = ref(null);

// Função para buscar conversas
const fetchConversations = async () => {
  try {
    loadingConversations.value = true;
    conversationsLoadError.value = '';
    if (props.currentConversation) {
      conversations.value = [props.currentConversation];
    } else {
      const response = await conversationAPI.get({});
      conversations.value = response.data.data.payload;
    }
  } catch {
    conversationsLoadError.value = t('PIPELINE.FORM.CONVERSATION.LOAD_ERROR');
  } finally {
    loadingConversations.value = false;
  }
};

const fetchAgents = async () => {
  try {
    loadingAgents.value = true;
    agentsLoadError.value = '';
    await store.dispatch('agents/get');
  } catch {
    agentsLoadError.value = t('PIPELINE.FORM.AGENT.LOAD_ERROR');
  } finally {
    loadingAgents.value = false;
  }
};

onMounted(async () => {
  if (props.initialData?.item_details?.offers?.length) {
    showValueInput.value = true;
    offers.value = props.initialData.item_details.offers.map(offer => ({
      ...offer,
      currency: currencyOptionFor(offer.currency),
    }));
    syncOfferTotals();
  } else if (
    initialValue !== null &&
    initialValue !== undefined &&
    initialValue !== ''
  ) {
    // Imported and automated cards may only have root expected_revenue.
    showValueInput.value = true;
    offers.value = [
      {
        description: '',
        value: initialValue,
        currency: initialCurrency,
      },
    ];
    syncOfferTotals();
  }

  // Establish the baseline before starting lookup requests. A user edit made
  // while a deferred lookup is pending must never be absorbed into the
  // initial draft snapshot.
  initialDraftSnapshot.value = captureDraftSnapshot();

  const lookups = [fetchAgents()];
  if (showConversationInput.value) lookups.push(fetchConversations());
  await Promise.all(lookups);
});

const validateCurrencyInput = () => {
  if (!newOffer.value.value) return;

  const number = parseLocalizedNumber(
    newOffer.value.value,
    activeLocale.value,
    {
      currency: selectedCurrencyCode.value,
    }
  );
  if (number === null || number < 0) {
    formErrors.value.value = t('PIPELINE.ERRORS.VALUE_INVALID');
    return;
  }

  delete formErrors.value.value;
  newOffer.value.value = formatLocalizedCurrency(number, {
    currency: selectedCurrencyCode.value,
    locale: activeLocale.value,
  });
};

watch(showValueInput, newValue => {
  if (!newValue) {
    if (offers.value.length > 0) {
      showValueInput.value = true;
      emitter.emit('newToastMessage', {
        message: t('PIPELINE.FORM.VALUE.REMOVE_OFFERS_FIRST'),
        type: 'warning',
      });
      return;
    }
    form.value.item_details.value = null;
    newOffer.value.value = '';
  }
});

watch(showConversationInput, newValue => {
  if (!newValue) {
    form.value.item_details.conversation_id = null;
  } else {
    fetchConversations();
  }
});

// Watch para limpar agendamento quando desmarcado
watch(showScheduleInput, newValue => {
  if (!newValue) {
    scheduleDate.value = '';
    scheduleTime.value = '09:00';
  }
});

watch(
  () => selectedPipeline.value?.id,
  (pipelineId, previousPipelineId) => {
    if (!pipelineId) return;

    form.value.pipeline_id = pipelineId;
    const currentStageIsAvailable = availableStages.value.some(
      stage => String(stage.id) === String(form.value.pipeline_stage)
    );
    if (
      (previousPipelineId &&
        String(previousPipelineId) !== String(pipelineId)) ||
      !currentStageIsAvailable
    ) {
      form.value.pipeline_stage = availableStages.value[0]?.id || '';
    }
  },
  { immediate: true }
);

// Adicionar watch para sincronizar o título em tempo real
watch(
  () => form.value.title,
  newTitle => {
    if (form.value.item_details) {
      form.value.item_details.title = newTitle;
    }
  }
);

// Adicionar watch para sincronizar a descrição em tempo real
watch(
  () => form.value.description,
  newDescription => {
    if (form.value.item_details) {
      form.value.item_details.description = newDescription;
    }
  }
);

// Modificar a função validateForm
const validateForm = () => {
  const errors = {};

  if (!form.value.title?.trim()) {
    errors.title = t('PIPELINE.ERRORS.TITLE_REQUIRED');
  }

  if (!form.value.pipeline_id) {
    errors.pipeline_id = t('PIPELINE.ERRORS.PIPELINE_REQUIRED');
  }

  if (!form.value.pipeline_stage) {
    errors.pipeline_stage = t('PIPELINE.ERRORS.STAGE_REQUIRED');
  } else if (
    !availableStages.value.some(
      stage => String(stage.id) === String(form.value.pipeline_stage)
    )
  ) {
    errors.pipeline_stage = t('PIPELINE.ERRORS.STAGE_INVALID');
  }

  if (showScheduleInput.value) {
    const deadline = zonedDateTimeToUtc(
      scheduleDate.value,
      scheduleTime.value,
      reportingTimezone.value
    );
    if (!scheduleDate.value) {
      errors.schedule = t('PIPELINE.ERRORS.SCHEDULE_DATE_REQUIRED');
    } else if (!deadline) {
      errors.schedule = t('PIPELINE.ERRORS.SCHEDULE_INVALID');
    } else if (
      deadline.getTime() < Date.now() &&
      !scheduleMatchesHistoricalDeadline()
    ) {
      errors.schedule = t('PIPELINE.ERRORS.SCHEDULE_IN_PAST');
    }
  }

  const offerCurrencies = new Set(
    offers.value.map(offer => currencyCodeFrom(offer.currency)).filter(Boolean)
  );
  if (offerCurrencies.size > 1) {
    errors.currency = t('PIPELINE.ERRORS.CURRENCY_MISMATCH');
  }

  formErrors.value = errors;
  if (Object.keys(errors).length > 0) {
    emitter.emit('newToastMessage', {
      message: errors[Object.keys(errors)[0]],
      type: 'error',
    });
    nextTick(() => {
      const errorField = document.querySelector(
        '[data-pipeline-form-error="true"] input, [data-pipeline-form-error="true"] select, [data-pipeline-form-error="true"] textarea, [data-pipeline-form-error="true"]'
      );
      errorField?.focus();
    });
    return errors;
  }

  return null;
};

const priorityOptions = [
  { id: 'none', name: t('PIPELINE.PRIORITY_LABELS.NONE') },
  { id: 'low', name: t('PIPELINE.PRIORITY_LABELS.LOW') },
  { id: 'medium', name: t('PIPELINE.PRIORITY_LABELS.MEDIUM') },
  { id: 'high', name: t('PIPELINE.PRIORITY_LABELS.HIGH') },
  { id: 'urgent', name: t('PIPELINE.PRIORITY_LABELS.URGENT') },
];

// Adds the pending offer through the same validation path used by submit.
const addOffer = () => {
  if (!newOffer.value.value) return false;

  const parsedValue = parseLocalizedNumber(
    newOffer.value.value,
    activeLocale.value,
    { currency: selectedCurrencyCode.value }
  );
  if (parsedValue === null || parsedValue < 0) {
    formErrors.value.value = t('PIPELINE.ERRORS.VALUE_INVALID');
    return false;
  }

  const establishedCurrency = currencyCodeFrom(offers.value[0]?.currency);
  if (
    establishedCurrency &&
    establishedCurrency !== selectedCurrencyCode.value
  ) {
    formErrors.value.currency = t('PIPELINE.ERRORS.CURRENCY_MISMATCH');
    return false;
  }

  const entry = {
    description: newOffer.value.description,
    value: parsedValue,
    currency: newOffer.value.currency,
  };
  const rawUrl = (newOffer.value.url || '').trim();
  if (rawUrl) {
    try {
      const parsedUrl = new URL(rawUrl);
      if (!['http:', 'https:'].includes(parsedUrl.protocol)) throw new Error();
      entry.url = parsedUrl.toString();
    } catch {
      formErrors.value.offerUrl = t('PIPELINE.ERRORS.OFFER_URL_INVALID');
      return false;
    }
  }

  delete formErrors.value.value;
  delete formErrors.value.offerUrl;
  delete formErrors.value.currency;
  offers.value.push(entry);
  syncOfferTotals();

  newOffer.value = {
    description: '',
    value: '',
    currency: offers.value[0].currency,
    url: '',
  };
  return true;
};

// Modificar handleSubmit
const handleSubmit = async e => {
  e.preventDefault();
  if (loading.value) return;
  const errors = validateForm();
  if (errors) return;

  try {
    loading.value = true;

    form.value.item_details.title = form.value.title;
    form.value.item_details.description = form.value.description;

    let finalItemDetails = {};

    if (props.isEditing && props.initialData?.item_details) {
      finalItemDetails = JSON.parse(
        JSON.stringify(props.initialData.item_details)
      );

      const fieldsToPreserve = [
        'notes',
        'checklist',
        'attachments',
        'activities',
        'history',
      ];
      fieldsToPreserve.forEach(field => {
        if (props.initialData.item_details[field]) {
          finalItemDetails[field] = props.initialData.item_details[field];
        }
      });
    }

    const fieldsToUpdate = [
      'title',
      'description',
      'value',
      'currency',
      'priority',
      'agent_id',
      'conversation_id',
    ];

    fieldsToUpdate.forEach(field => {
      if (field in form.value.item_details) {
        finalItemDetails[field] = form.value.item_details[field];
      }
    });

    finalItemDetails.title = form.value.title;

    // Auto-add pending value if user typed a value but didn't click "+"
    if (showValueInput.value && newOffer.value.value && !addOffer()) {
      return;
    }

    finalItemDetails.offers = offers.value;

    // Preserve the exact historical value (including legacy `deadline` and
    // midnight instants) when the user edits unrelated card fields.
    if (!scheduleMatchesHistoricalDeadline()) {
      if (showScheduleInput.value && scheduleDate.value) {
        const deadlineDate = zonedDateTimeToUtc(
          scheduleDate.value,
          scheduleTime.value,
          reportingTimezone.value
        );
        finalItemDetails.deadline_at = deadlineDate.toISOString();
        finalItemDetails.scheduling_type = 'deadline';
      } else {
        // Limpar deadline se desmarcado
        finalItemDetails.deadline_at = null;
        finalItemDetails.deadline = null;
        finalItemDetails.scheduling_type = null;
      }
    }

    if (offers.value.length > 0) {
      // Sum all offer values
      finalItemDetails.value = offers.value.reduce(
        (sum, offer) => sum + (Number(offer.value) || 0),
        0
      );
      finalItemDetails.currency = offers.value[0].currency;
    }

    const payloadCurrency =
      currencyCodeFrom(finalItemDetails.currency) || initialCurrencyCode;

    const payload = {
      pipeline_id: selectedPipeline.value?.id || props.pipelineId,
      pipeline_stage: form.value.pipeline_stage,
      position: props.position,
      owner_id: form.value.item_details.agent_id || null,
      priority: form.value.item_details.priority || 'none',
      expected_revenue: finalItemDetails.value ?? null,
      currency: payloadCurrency,
      // Fase 2.2 — previsão de fechamento (date, nullable)
      forecast_close_date: forecastCloseDate.value || null,
      item_details: finalItemDetails,
    };

    if (!payload.pipeline_id) {
      emitter.emit('newToastMessage', {
        message: t('PIPELINE.ERRORS.PIPELINE_REQUIRED'),
        type: 'error',
      });
      loading.value = false;
      return;
    }

    const result = props.isEditing
      ? await store.dispatch('pipelineCards/updateItemDetails', {
          itemId: props.initialData.id,
          details: payload,
        })
      : await store.dispatch('pipelineCards/createPipelineCardItem', payload);

    emitter.emit('newToastMessage', {
      message: props.isEditing
        ? t('PIPELINE.MESSAGES.ITEM_UPDATED_SUCCESS')
        : t('PIPELINE.MESSAGES.ITEM_CREATED_SUCCESS'),
      type: 'success',
    });

    initialDraftSnapshot.value = captureDraftSnapshot();
    emit('saved', result);
    emit('close');
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_SAVE_ERROR'),
      type: 'error',
    });
  } finally {
    loading.value = false;
  }
};

const requestClose = async () => {
  if (loading.value) return false;
  if (!isDirty.value) return true;
  return discardConfirmation.value.showConfirmation();
};

const handleClose = async () => {
  if (await requestClose()) emit('close');
};

defineExpose({ requestClose, isDirty });

// Funções para gerenciar ofertas
const removeOffer = index => {
  offers.value.splice(index, 1);
  delete formErrors.value.currency;
  syncOfferTotals();
};

// Fase 3.3 — valores rápidos pra preencher oferta com 1 click
// Lê de pipeline.settings.quick_values se configurado, senão usa default.
const DEFAULT_QUICK_VALUES = [1000, 5000, 10000, 25000, 50000];

const quickValues = computed(() => {
  const configured = selectedPipeline.value?.settings?.quick_values;
  if (Array.isArray(configured) && configured.length > 0) {
    return configured
      .map(v => Number(v))
      .filter(v => Number.isFinite(v) && v > 0);
  }
  return DEFAULT_QUICK_VALUES;
});

const applyQuickValue = value => {
  newOffer.value.value = formatLocalizedCurrency(value, {
    currency: selectedCurrencyCode.value,
    locale: activeLocale.value,
  });
};

// Fase 3.2 — marca oferta como padrão para métricas.
// Click na estrela ativada desmarca (toggle). Click em outra, transfere.
const setOfferAsDefault = index => {
  const target = offers.value[index];
  if (!target) return;

  const currentlyActive = Boolean(target.is_default);
  offers.value = offers.value.map((offer, i) => ({
    ...offer,
    is_default: !currentlyActive && i === index,
  }));
};

const formatCurrencyValue = (value, currency) => {
  if (value === null || value === undefined || value === '') return '-';

  try {
    return formatLocalizedCurrency(value, {
      locale: activeLocale.value,
      currency: currency?.code || currency || 'BRL',
    });
  } catch (error) {
    return value.toString();
  }
};
</script>

<template>
  <form
    class="flex flex-col h-full min-h-0 !p-0 !self-auto"
    @submit.prevent="handleSubmit"
  >
    <!-- Main Content - Scrollable -->
    <main class="flex-grow min-h-0 overflow-y-auto px-5 py-4 space-y-4">
      <!-- Title (full width, primary field) -->
      <div class="space-y-1.5">
        <label
          for="pipeline-card-title"
          class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
        >
          {{ t('PIPELINE.FORM.TITLE.LABEL') }}
          <span class="text-n-ruby-9" aria-hidden="true">
            {{ t('PIPELINE.FORM.REQUIRED_MARK') }}
          </span>
        </label>
        <Input
          id="pipeline-card-title"
          v-model="form.title"
          name="pipeline_card_title"
          type="text"
          autocomplete="off"
          class="w-full"
          custom-input-class="!h-11"
          :placeholder="t('PIPELINE.FORM.TITLE.PLACEHOLDER')"
          :message-type="formErrors.title ? 'error' : 'info'"
          :aria-invalid="Boolean(formErrors.title)"
          :aria-describedby="
            formErrors.title ? 'pipeline-card-title-error' : null
          "
          :data-pipeline-form-error="Boolean(formErrors.title) || null"
          required
        />
        <p
          v-if="formErrors.title"
          id="pipeline-card-title-error"
          role="alert"
          class="text-xs text-n-ruby-11"
        >
          {{ formErrors.title }}
        </p>
      </div>

      <!-- Value + Description row -->
      <div class="space-y-4">
        <!-- Value toggle -->
        <div class="space-y-1.5">
          <div class="flex items-center justify-between">
            <span
              id="pipeline-card-value-label"
              class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
            >
              {{ t('PIPELINE.FORM.VALUE.LABEL') }}
            </span>
            <label class="flex items-center gap-2 cursor-pointer group">
              <input
                id="has-value"
                v-model="showValueInput"
                type="checkbox"
                class="h-3.5 w-3.5 rounded border-n-weak text-n-brand focus:ring-n-brand bg-n-solid-2 transition-colors"
              />
              <span
                class="text-xs text-n-slate-10 group-hover:text-n-slate-12 transition-colors"
              >
                {{ t('PIPELINE.FORM.VALUE.HAS_VALUE') }}
              </span>
            </label>
          </div>
          <div v-if="showValueInput" class="flex flex-wrap items-center gap-2">
            <div class="relative min-w-[12rem] flex-1">
              <Input
                id="pipeline-card-offer-value"
                v-model="newOffer.value"
                name="pipeline_card_offer_value"
                type="text"
                inputmode="decimal"
                autocomplete="off"
                class="w-full"
                custom-input-class="!h-11"
                :placeholder="t('PIPELINE.FORM.VALUE.PLACEHOLDER')"
                :message-type="formErrors.value ? 'error' : 'info'"
                aria-labelledby="pipeline-card-value-label"
                :aria-invalid="Boolean(formErrors.value)"
                :aria-describedby="
                  formErrors.value ? 'pipeline-card-value-error' : null
                "
                @blur="validateCurrencyInput"
              />
            </div>
            <label class="sr-only" for="pipeline-card-offer-currency">
              {{ t('PIPELINE.FORM.VALUE.CURRENCY') }}
            </label>
            <Select
              v-model="selectedCurrencyCode"
              input-id="pipeline-card-offer-currency"
              class="shrink-0"
              name="pipeline_card_offer_currency"
              :options="currencySelectOptions"
              :aria-label="t('PIPELINE.FORM.VALUE.CURRENCY')"
              :aria-invalid="Boolean(formErrors.currency)"
              :aria-describedby="
                formErrors.currency ? 'pipeline-card-currency-error' : null
              "
              :disabled="offers.length > 0"
            />
            <button
              type="button"
              class="size-11 shrink-0 inline-flex items-center justify-center bg-n-brand hover:brightness-110 text-white rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2"
              :aria-label="t('PIPELINE.FORM.VALUE.ADD_OFFER')"
              @click="addOffer"
            >
              <fluent-icon icon="add" size="16" aria-hidden="true" />
            </button>
          </div>
          <p
            v-if="formErrors.value"
            id="pipeline-card-value-error"
            role="alert"
            class="text-xs text-n-ruby-11"
          >
            {{ formErrors.value }}
          </p>
          <p
            v-if="formErrors.currency"
            id="pipeline-card-currency-error"
            role="alert"
            class="text-xs text-n-ruby-11"
          >
            {{ formErrors.currency }}
          </p>
          <!-- Fase 3.3 — quick value presets (agilidade pra valores comuns) -->
          <div
            v-if="showValueInput && quickValues.length > 0"
            class="flex flex-wrap gap-1.5 mt-1"
          >
            <button
              v-for="preset in quickValues"
              :key="preset"
              type="button"
              class="min-h-11 px-3 py-1 text-xs rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 border border-n-weak transition-colors focus-visible:ring-2 focus-visible:ring-n-brand"
              :title="t('PIPELINE.FORM.VALUE.QUICK_VALUE_TOOLTIP')"
              @click="() => applyQuickValue(preset)"
            >
              {{ formatCurrencyValue(preset, newOffer.currency) }}
            </button>
          </div>
          <!-- Fase 3.5 — descrição + URL opcional da oferta -->
          <div
            v-if="showValueInput"
            class="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2"
          >
            <div class="space-y-1">
              <label class="sr-only" for="pipeline-card-offer-description">
                {{ t('PIPELINE.FORM.VALUE.OFFER_DESCRIPTION') }}
              </label>
              <Input
                id="pipeline-card-offer-description"
                v-model="newOffer.description"
                name="pipeline_card_offer_description"
                type="text"
                autocomplete="off"
                class="w-full"
                custom-input-class="!h-11"
                :placeholder="
                  t('PIPELINE.FORM.VALUE.OFFER_DESCRIPTION_PLACEHOLDER')
                "
              />
            </div>
            <div class="space-y-1">
              <label class="sr-only" for="pipeline-card-offer-url">
                {{ t('PIPELINE.FORM.VALUE.OFFER_URL') }}
              </label>
              <Input
                id="pipeline-card-offer-url"
                v-model="newOffer.url"
                name="pipeline_card_offer_url"
                type="url"
                inputmode="url"
                autocomplete="off"
                class="w-full"
                custom-input-class="!h-11"
                :placeholder="t('PIPELINE.FORM.VALUE.OFFER_URL_PLACEHOLDER')"
                :message-type="formErrors.offerUrl ? 'error' : 'info'"
                :aria-invalid="Boolean(formErrors.offerUrl)"
                :aria-describedby="
                  formErrors.offerUrl ? 'pipeline-card-offer-url-error' : null
                "
              />
              <p
                v-if="formErrors.offerUrl"
                id="pipeline-card-offer-url-error"
                role="alert"
                class="text-xs text-n-ruby-11"
              >
                {{ formErrors.offerUrl }}
              </p>
            </div>
          </div>
          <div
            v-if="!showValueInput"
            class="bg-n-alpha-1 border border-n-weak rounded-lg px-3 py-2 text-sm text-n-slate-9 italic"
          >
            {{ t('PIPELINE.FORM.VALUE.NO_VALUE') }}
          </div>
        </div>

        <!-- Offers list -->
        <div
          v-if="offers.length > 0"
          class="flex flex-wrap gap-2 p-3 bg-n-solid-2 rounded-lg border border-n-weak border-dashed"
        >
          <div
            v-for="(offer, index) in offers"
            :key="index"
            class="inline-flex items-center gap-1 bg-n-alpha-1 text-n-brand border border-n-weak rounded-full pl-2 pr-2 py-1 text-sm"
            :class="{ 'border-n-amber-9 bg-n-amber-3': offer.is_default }"
          >
            <button
              type="button"
              :title="
                offer.is_default
                  ? t('PIPELINE.FORM.VALUE.DEFAULT_OFFER_ACTIVE')
                  : t('PIPELINE.FORM.VALUE.DEFAULT_OFFER_SET')
              "
              class="size-11 inline-flex items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-n-brand"
              :class="
                offer.is_default
                  ? 'text-n-amber-11 hover:text-n-amber-12'
                  : 'text-n-slate-10 hover:text-n-amber-11'
              "
              :aria-label="
                offer.is_default
                  ? t('PIPELINE.FORM.VALUE.DEFAULT_OFFER_ACTIVE')
                  : t('PIPELINE.FORM.VALUE.DEFAULT_OFFER_SET')
              "
              :aria-pressed="Boolean(offer.is_default)"
              @click="() => setOfferAsDefault(index)"
            >
              <fluent-icon
                :icon="offer.is_default ? 'star-filled' : 'star'"
                size="16"
                aria-hidden="true"
              />
            </button>
            <span class="font-medium">{{
              formatCurrencyValue(offer.value, offer.currency)
            }}</span>
            <a
              v-if="offer.url"
              :href="offer.url"
              :title="offer.url"
              target="_blank"
              rel="noopener noreferrer"
              class="size-11 inline-flex items-center justify-center hover:bg-n-alpha-2 rounded-full text-n-brand hover:text-n-brand-11 transition-colors focus-visible:ring-2 focus-visible:ring-n-brand"
              :aria-label="t('PIPELINE.FORM.VALUE.OPEN_OFFER_URL')"
              @click.stop
            >
              <fluent-icon icon="link" size="16" aria-hidden="true" />
            </a>
            <button
              type="button"
              class="size-11 inline-flex items-center justify-center hover:bg-n-alpha-2 rounded-full text-n-brand hover:text-n-ruby-11 transition-colors focus-visible:ring-2 focus-visible:ring-n-brand"
              :aria-label="t('PIPELINE.FORM.VALUE.REMOVE_OFFER')"
              @click="() => removeOffer(index)"
            >
              <fluent-icon icon="dismiss" size="16" aria-hidden="true" />
            </button>
          </div>
        </div>

        <!-- Description -->
        <div class="space-y-1.5">
          <label
            for="pipeline-card-description"
            class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
          >
            {{ t('PIPELINE.FORM.DESCRIPTION.LABEL') }}
          </label>
          <TextArea
            id="pipeline-card-description"
            v-model="form.description"
            class="w-full"
            :placeholder="t('PIPELINE.FORM.DESCRIPTION.PLACEHOLDER')"
          />
        </div>
      </div>

      <!-- Divider -->
      <div class="border-t border-n-weak" />

      <!-- Pipeline & Stage (merged row) -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div class="space-y-1.5">
          <label
            id="pipeline-card-pipeline-label"
            class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
          >
            {{ t('PIPELINE.FORM.PIPELINE.LABEL') }}
          </label>
          <PipelineSelector
            class="w-full"
            aria-labelledby="pipeline-card-pipeline-label"
          />
        </div>
        <div class="space-y-1.5">
          <label
            for="pipeline-card-stage"
            class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
          >
            {{ t('PIPELINE.FORM.STAGE.LABEL') }}
          </label>
          <Select
            v-model="form.pipeline_stage"
            input-id="pipeline-card-stage"
            name="pipeline_card_stage"
            class="w-full [&>select]:w-full"
            :options="stageOptions"
            :placeholder="t('PIPELINE.FORM.STAGE.PLACEHOLDER')"
            :disabled="!selectedPipeline?.id"
            :error="formErrors.pipeline_stage"
            :aria-label="t('PIPELINE.FORM.STAGE.LABEL')"
            :aria-describedby="
              formErrors.pipeline_stage ? 'pipeline-card-stage-error' : null
            "
            :data-pipeline-form-error="
              Boolean(formErrors.pipeline_stage) || null
            "
          />
          <p
            v-if="formErrors.pipeline_stage"
            id="pipeline-card-stage-error"
            role="alert"
            class="text-xs text-n-ruby-11"
          >
            {{ formErrors.pipeline_stage }}
          </p>
        </div>
      </div>

      <!-- Priority & Agent (merged row) -->
      <div class="space-y-3">
        <!-- Priority (full width so buttons don't wrap) -->
        <div class="space-y-1.5">
          <label
            class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
          >
            {{ t('PIPELINE.FORM.PRIORITY.LABEL') }}
          </label>
          <div
            class="grid grid-cols-2 sm:grid-cols-5 gap-2"
            role="group"
            :aria-label="t('PIPELINE.FORM.PRIORITY.LABEL')"
          >
            <button
              v-for="option in priorityOptions"
              :key="option.id"
              type="button"
              class="min-h-11 px-2 py-1.5 text-xs font-medium rounded-lg transition-colors border text-center focus-visible:ring-2 focus-visible:ring-n-brand"
              :class="[
                form.item_details.priority === option.id
                  ? 'bg-n-brand text-white border-n-brand'
                  : {
                      'bg-n-solid-2 border-n-weak text-n-slate-11 hover:bg-n-alpha-2':
                        option.id === 'none',
                      'bg-n-solid-2 border-n-weak text-n-teal-11 hover:bg-n-teal-3':
                        option.id === 'low',
                      'bg-n-solid-2 border-n-weak text-n-amber-11 hover:bg-n-amber-3':
                        option.id === 'medium',
                      'bg-n-solid-2 border-n-weak text-n-orange-11 hover:bg-n-orange-3':
                        option.id === 'high',
                      'bg-n-solid-2 border-n-weak text-n-ruby-11 hover:bg-n-ruby-3':
                        option.id === 'urgent',
                    },
              ]"
              :aria-pressed="form.item_details.priority === option.id"
              @click="form.item_details.priority = option.id"
            >
              {{ option.name }}
            </button>
          </div>
        </div>

        <!-- Agent -->
        <div class="space-y-1.5">
          <label
            for="pipeline-card-agent"
            class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
          >
            {{ t('PIPELINE.FORM.AGENT.LABEL') }}
          </label>
          <Select
            v-model="form.item_details.agent_id"
            input-id="pipeline-card-agent"
            name="pipeline_card_agent"
            class="w-full [&>select]:w-full"
            :options="agentOptions"
            :aria-label="t('PIPELINE.FORM.AGENT.LABEL')"
            :disabled="loadingAgents"
          />
          <div
            v-if="agentsLoadError"
            role="alert"
            class="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-n-ruby-3 px-3 py-2 text-sm text-n-ruby-11"
          >
            <span>{{ agentsLoadError }}</span>
            <Button
              type="button"
              variant="ghost"
              color="ruby"
              size="md"
              class="min-h-11"
              :disabled="loadingAgents"
              @click="fetchAgents"
            >
              {{ t('PIPELINE.ERRORS.RETRY') }}
            </Button>
          </div>
        </div>
      </div>

      <!-- Divider -->
      <div class="border-t border-n-weak" />

      <!-- Schedule toggle -->
      <div class="space-y-3">
        <label class="flex items-center gap-2.5 cursor-pointer group">
          <input
            id="has-schedule"
            v-model="showScheduleInput"
            type="checkbox"
            class="h-4 w-4 rounded border-n-weak text-n-brand focus:ring-n-brand transition-colors"
          />
          <div class="flex items-center gap-2">
            <fluent-icon icon="calendar" size="16" class="text-n-slate-10" />
            <span
              class="text-sm font-medium text-n-slate-12 group-hover:text-n-brand transition-colors"
            >
              {{ t('PIPELINE.FORM.SCHEDULE.HAS_SCHEDULE') }}
            </span>
          </div>
        </label>

        <div v-if="showScheduleInput" class="pl-7 space-y-3">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div class="space-y-1.5">
              <label
                for="pipeline-card-schedule-date"
                class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
              >
                {{ t('PIPELINE.FORM.SCHEDULE.DATE_LABEL') }}
              </label>
              <Input
                id="pipeline-card-schedule-date"
                v-model="scheduleDate"
                name="pipeline_card_schedule_date"
                type="date"
                class="w-full"
                custom-input-class="!h-11"
                :message-type="formErrors.schedule ? 'error' : 'info'"
                :aria-invalid="Boolean(formErrors.schedule)"
                :aria-describedby="
                  formErrors.schedule
                    ? 'pipeline-card-schedule-error'
                    : 'pipeline-card-timezone'
                "
                :data-pipeline-form-error="Boolean(formErrors.schedule) || null"
              />
            </div>
            <div class="space-y-1.5">
              <label
                for="pipeline-card-schedule-time"
                class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
              >
                {{ t('PIPELINE.FORM.SCHEDULE.TIME_LABEL') }}
              </label>
              <Input
                id="pipeline-card-schedule-time"
                v-model="scheduleTime"
                name="pipeline_card_schedule_time"
                type="time"
                class="w-full"
                custom-input-class="!h-11"
              />
            </div>
          </div>
          <p
            v-if="formErrors.schedule"
            id="pipeline-card-schedule-error"
            role="alert"
            class="text-xs text-n-ruby-11"
          >
            {{ formErrors.schedule }}
          </p>
          <p id="pipeline-card-timezone" class="text-xs text-n-slate-10">
            {{
              t('PIPELINE.FORM.SCHEDULE.TIMEZONE', {
                timezone: reportingTimezone,
              })
            }}
          </p>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="time in quickTimeOptions"
              :key="time"
              type="button"
              class="min-h-11 min-w-11 px-2.5 py-1 text-xs font-medium rounded-md transition-colors border focus-visible:ring-2 focus-visible:ring-n-brand"
              :class="
                scheduleTime === time
                  ? 'bg-n-brand text-white border-n-brand'
                  : 'bg-n-solid-2 border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
              "
              :aria-pressed="scheduleTime === time"
              @click="scheduleTime = time"
            >
              {{ time }}
            </button>
          </div>
        </div>
      </div>

      <!-- Fase 2.2 — Previsão de fechamento do deal -->
      <div class="space-y-3">
        <label
          for="pipeline-card-forecast-date"
          class="flex items-center gap-2 cursor-pointer group"
        >
          <fluent-icon
            icon="calendar-clock"
            size="16"
            class="text-n-slate-10"
          />
          <span
            class="text-sm font-medium text-n-slate-12 group-hover:text-n-brand transition-colors"
          >
            {{ t('PIPELINE.FORM.FORECAST_CLOSE_DATE.LABEL') }}
          </span>
          <span class="text-xs text-n-slate-10">{{
            t('PIPELINE.FORM.OPTIONAL')
          }}</span>
        </label>
        <div class="pl-6">
          <Input
            id="pipeline-card-forecast-date"
            v-model="forecastCloseDate"
            name="pipeline_card_forecast_date"
            type="date"
            class="w-full"
            custom-input-class="!h-11"
          />
          <p class="text-xs text-n-slate-10 mt-1">
            {{ t('PIPELINE.FORM.FORECAST_CLOSE_DATE.HELP') }}
          </p>
        </div>
      </div>

      <!-- Conversation toggle -->
      <div class="space-y-3">
        <label class="flex items-center gap-2.5 cursor-pointer group">
          <input
            id="has-conversation"
            v-model="showConversationInput"
            type="checkbox"
            class="h-4 w-4 rounded border-n-weak text-n-brand focus:ring-n-brand transition-colors"
          />
          <div class="flex items-center gap-2">
            <fluent-icon icon="link" size="16" class="text-n-slate-10" />
            <span
              class="text-sm font-medium text-n-slate-12 group-hover:text-n-brand transition-colors"
            >
              {{ t('PIPELINE.FORM.CONVERSATION.HAS_CONVERSATION') }}
            </span>
          </div>
        </label>

        <div v-if="showConversationInput" class="pl-7">
          <Select
            v-model="form.item_details.conversation_id"
            input-id="pipeline-card-conversation"
            name="pipeline_card_conversation"
            class="w-full [&>select]:w-full"
            :options="conversationOptions"
            :aria-label="t('PIPELINE.FORM.CONVERSATION.LABEL')"
            :disabled="loadingConversations"
          />
          <div
            v-if="conversationsLoadError"
            role="alert"
            class="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-n-ruby-3 px-3 py-2 text-sm text-n-ruby-11"
          >
            <span>{{ conversationsLoadError }}</span>
            <Button
              type="button"
              variant="ghost"
              color="ruby"
              size="md"
              class="min-h-11"
              :disabled="loadingConversations"
              @click="fetchConversations"
            >
              {{ t('PIPELINE.ERRORS.RETRY') }}
            </Button>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer Actions (sticky bottom) -->
    <div
      class="flex items-center justify-end px-5 py-3 border-t border-n-weak bg-n-solid-1 flex-shrink-0 gap-2"
    >
      <Button
        type="button"
        variant="ghost"
        color="slate"
        size="md"
        class="min-h-11"
        @click="handleClose"
      >
        {{ t('PIPELINE.CANCEL') }}
      </Button>
      <Button
        type="submit"
        variant="solid"
        color="blue"
        size="md"
        class="min-h-11"
        :is-loading="loading"
        :disabled="loading"
      >
        {{ t('PIPELINE.SAVE') }}
      </Button>
    </div>
  </form>

  <ConfirmModal
    ref="discardConfirmation"
    :title="t('PIPELINE.FORM.DISCARD.TITLE')"
    :description="t('PIPELINE.FORM.DISCARD.DESCRIPTION')"
    :confirm-label="t('PIPELINE.FORM.DISCARD.CONFIRM')"
    :cancel-label="t('PIPELINE.FORM.DISCARD.KEEP_EDITING')"
  />
</template>
